import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UsersService } from '../../../../core/services/users.service';
import { User } from '../../../../core/models/user.interface';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../../../core/services/auth.service';
import { LoaderService } from '../../../../core/shared/services/loader.service';
import { BaseResponse } from '../../../../core/models/base.response';
import { BookAppointmentDto } from '../../../../core/models/coach.dto';
import { FormsModule } from '@angular/forms';

interface TimeSlot {
  time: string; // HH:mm
  displayTime: string; // Formato para mostrar (ej: 09:00 AM)
  isBooked: boolean;
  isPastTime?: boolean; // Nueva propiedad para horarios pasados
  selected?: boolean;
}

interface DisplayableDate {
  date: Date;
  dayName: string; // Ej: LUN
  dayNumber: string; // Ej: 02
  selected?: boolean;
  isToday?: boolean;
  available?: boolean; // Indica si hay disponibilidad para esta fecha
}

@Component({
  selector: 'app-schedule-appointment',
  standalone: true,
  imports: [CommonModule, MatIconModule, FormsModule, RouterModule],
  templateUrl: './schedule-appointment.component.html',
  styleUrl: './schedule-appointment.component.scss'
})
export class ScheduleAppointmentComponent implements OnInit {
  mentor: User | null = null;

  isLoading = true;
  mentorId: string = '';

  selectedDate: Date | null = null; // Usaremos objeto Date
  selectedTimeSlot: TimeSlot | null = null;

  hourlyRate: string = 'S/.250'; // Esto podría venir del perfil del mentor en el futuro

  // Modales
  showSummaryModal: boolean = false;
  showSuccessModal: boolean = false;
  acceptTerms: boolean = false;
  countdown: string = '-02:03m'; // Tiempo restante para confirmar

  // Configuración de slots de tiempo
  private morningSlotsTemplate: string[] = ['09:00', '09:30', '10:00', '10:30'];
  private afternoonSlotsTemplate: string[] = ['14:00', '14:30', '15:00', '15:30', '16:00', '16:30'];
  private nightSlotsTemplate: string[] = ['19:00', '19:30', '20:00', '20:30'];

  timeFilters = [
    { name: 'Mañana', active: true, slotsKey: 'morning' as const },
    { name: 'Tarde', active: false, slotsKey: 'afternoon' as const },
    { name: 'Noche', active: false, slotsKey: 'night' as const }
  ];
  activeTimeFilterKey: 'morning' | 'afternoon' | 'night' = 'morning';

  displayableDates: DisplayableDate[] = [];
  displayableTimeSlots: TimeSlot[] = [];

  private authService = inject(AuthService);
  private loaderService = inject(LoaderService);
  private usersService = inject(UsersService);

  // Nueva propiedad para rastrear el offset de días
  currentDayOffset: number = 0;
  diasDeNavegacion: number = 14; // Número de días a mostrar por defecto

  currentUser: any = this.authService.getUser();

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {

    console.log('Usuario actual:', this.currentUser);
    if (!this.currentUser) {
      // Redirigir si no hay usuario logueado, o manejar según la lógica de la app
      this.router.navigate(['/auth']);
      return;
    }

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.mentorId = params['id'];
        this.loadMentorDetails();
      } else {
        this.router.navigate(['/dashboard/mentors']);
      }
    });
  }

  loadMentorDetails(): void {
    this.isLoading = true;
    this.loaderService.show();
    this.usersService.findById(this.mentorId).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.mentor = response.data;
          console.log('Detalles del mentor cargados:', this.mentor);
          this.generateDisplayableDates(14); // Generar para los próximos 14 días
          // Si se carga el mentor, seleccionar la primera fecha disponible por defecto
          if (this.displayableDates.length > 0) {
            this.selectDate(this.displayableDates[0]);
          }
        }
        this.isLoading = false;
        this.loaderService.hide();

      },
      error: (err) => {
        console.error('Error al cargar detalles del mentor:', err);
        this.isLoading = false;
        this.loaderService.hide();
        this.router.navigate(['/dashboard/mentors']);
      }
    });
  }

  generateDisplayableDates(numberOfDays: number): void {
    this.displayableDates = []; // Limpiar fechas anteriores
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalizar a medianoche

    let availableDays = this.mentor?.coachAvailability || [];

    for (let i = 0; i < numberOfDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + this.currentDayOffset + i);
      this.displayableDates.push({
        date: date,
        dayName: this.getDayName(date),
        dayNumber: formatDate(date, 'dd', 'en-US'),
        isToday: date.toDateString() === today.toDateString(),
        available: availableDays.some((item) => {
          return date.toISOString().substring(0, 10) === item.day;
        })
      });
    }

  }

  private getDayName(date: Date): string {
    return ['DOM', 'LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB'][date.getDay()];
  }

  selectDate(dateToSelect: DisplayableDate): void {
    this.displayableDates.forEach(d => d.selected = false);
    dateToSelect.selected = true;
    this.selectedDate = dateToSelect.date;
    console.log('Fecha seleccionada:', this.selectedDate);
    this.selectedTimeSlot = null; // Resetear hora seleccionada

    // Actualizar los slots de tiempo disponibles para la fecha seleccionada
    const day = formatDate(this.selectedDate, 'yyyy-MM-dd', 'en-US');
    console.log('Buscando disponibilidad para el día:', day);

    const disponibilidad = this.mentor?.coachAvailability?.find(appt => appt.day === day);
    console.log('Disponibilidad encontrada:', disponibilidad);

    let horarios: string[] = disponibilidad?.times || [];
    let mañana = horarios.filter(time => {
      const [hour, minute] = time.split(':').map(Number);
      return (hour >= 7 && hour <= 12); // Filtrar solo horas de la mañana (07:00 a 12:00)
    });
    this.morningSlotsTemplate = mañana;
    console.log('Mañana:', mañana);

    let tarde = horarios.filter(time => {
      const [hour, minute] = time.split(':').map(Number);
      return (hour >= 13 && hour <= 17); // Filtrar solo horas de la tarde (13:00 a 17:00)
    });
    this.afternoonSlotsTemplate = tarde;
    console.log('Tarde:', tarde);

    let noche = horarios.filter(time => {
      const [hour, minute] = time.split(':').map(Number);
      return (hour >= 18 && hour <= 22); // Filtrar solo horas de la noche (18:00 a 22:00)
    });
    this.nightSlotsTemplate = noche;
    console.log('Noche:', noche);

    this.updateDisplayableTimeSlots();
  }

  selectTimeSlot(slotToSelect: TimeSlot): void {
    if (slotToSelect.isBooked) return;
    this.displayableTimeSlots.forEach(s => s.selected = false);
    slotToSelect.selected = true;
    this.selectedTimeSlot = slotToSelect;
  }

  selectTimeFilter(filterIndex: number): void {
    this.timeFilters.forEach(f => f.active = false);
    this.timeFilters[filterIndex].active = true;
    this.activeTimeFilterKey = this.timeFilters[filterIndex].slotsKey;
    this.selectedTimeSlot = null; // Resetear hora seleccionada
    this.updateDisplayableTimeSlots();
  }

  private formatTimeToDisplay(time: string): string { // time en HH:mm
    const [hour, minute] = time.split(':');
    const h = parseInt(hour, 10);
    const period = h >= 12 ? 'PM' : 'AM';
    const adjustedHour = h % 12 === 0 ? 12 : h % 12;
    return `${adjustedHour.toString().padStart(2, '0')}:${minute} ${period}`;
  }

  updateDisplayableTimeSlots(): void {
    if (!this.selectedDate || !this.mentor) {
      this.displayableTimeSlots = [];
      return;
    }

    let template: string[];
    if (this.activeTimeFilterKey === 'morning') template = this.morningSlotsTemplate;
    else if (this.activeTimeFilterKey === 'afternoon') template = this.afternoonSlotsTemplate;
    else template = this.nightSlotsTemplate;

    const selectedDateStr = formatDate(this.selectedDate, 'yyyy-MM-dd', 'en-US');
    const today = new Date();
    const isToday = this.selectedDate.toDateString() === today.toDateString();

    this.displayableTimeSlots = template.map(time => {
      const isBooked = this.mentor?.coachAppointments?.some(
        appt => appt.date === selectedDateStr && appt.time === time
      ) || false;

      // Verificar si el horario ya pasó (solo para el día actual)
      let isPastTime = false;
      if (isToday) {
        const [hours, minutes] = time.split(':').map(Number);
        const slotTime = new Date();
        slotTime.setHours(hours, minutes, 0, 0);

        // Considerar que un horario ya pasó si faltan menos de 30 minutos
        const currentTime = new Date();
        const thirtyMinutesFromNow = new Date(currentTime.getTime() + 30 * 60 * 1000);

        isPastTime = slotTime <= thirtyMinutesFromNow;
      }

      return {
        time: time,
        displayTime: this.formatTimeToDisplay(time),
        isBooked: isBooked || isPastTime, // Tratar horarios pasados como no disponibles
        isPastTime: isPastTime,
        selected: false
      };
    });
  }

  // Nuevo método para mostrar el modal de resumen antes de confirmar
  showSummary(): void {
    if (!this.selectedDate || !this.selectedTimeSlot || !this.mentorId || !this.currentUser) {
      alert('Por favor selecciona una fecha y hora para la cita.');
      return;
    }
    if (this.selectedTimeSlot.isBooked) {
      alert('El horario seleccionado ya no está disponible. Por favor, elija otro.');
      return;
    }

    this.acceptTerms = false; // Resetear el checkbox
    this.showSummaryModal = true;
  }

  // Método para cerrar el modal de resumen (cancelar)
  cancelReservation(): void {
    this.showSummaryModal = false;
  }

  // Método para confirmar la reserva después de ver el resumen
  confirmReservation(): void {
    if (!this.acceptTerms) {
      alert('Debes aceptar los términos y condiciones para continuar.');
      return;
    }

    this.showSummaryModal = false;
    this.isLoading = true;
    this.loaderService.show();

    const appointmentData = {
      date: formatDate(this.selectedDate!, 'yyyy-MM-dd', 'en-US'),
      time: this.formatTimeToDisplay(this.selectedTimeSlot!.time), // Enviar en formato AM/PM
      clientName: this.currentUser!.name, // Nombre del cliente
      clientId: this.currentUser!._id // ID del cliente
    };
    
    this.usersService.bookAppointment(this.mentorId, appointmentData, '').subscribe({
      next: (response: BaseResponse<User>) => {
        this.isLoading = false;
        this.loaderService.hide();

        // Actualizar localmente las citas del mentor si es necesario o recargar
        if (this.mentor && response.data.coachAppointments) {
          this.mentor.coachAppointments = response.data.coachAppointments;
        }

        // Mostrar modal de éxito
        this.showSuccessModal = true;
      },
      error: (err: any) => {
        this.isLoading = false;
        this.loaderService.hide();
        console.error('Error al programar la cita:', err);
        alert(err.error?.message || 'Error al programar la cita. Por favor, inténtelo de nuevo.');

        // Si el error es por slot ocupado, refrescar
        if (err.status === 400 && err.error?.message?.includes('no está disponible')) {
          this.loadMentorDetails(); // Recargar datos del mentor para obtener la última lista de citas
        }
      }
    });
  }

  // Método para manejar la acción después del éxito
  afterSuccess(goToCalendar: boolean): void {
    this.showSuccessModal = false;
    if (goToCalendar) {
      // Navegar al calendario (ajustar según la estructura de tu aplicación)
      this.router.navigate(['/dashboard/calendar']);
    } else {
      // Redirigir al home de mentores como se solicita en la tarea
      this.router.navigate(['/dashboard/mentors']);
    }
  }

  // Métodos de navegación existentes
  goToProfile(): void {
    if (this.mentorId) {
      this.router.navigate(['/dashboard/mentor', this.mentorId]);
    }
  }

  changeMentor(): void {
    this.router.navigate(['/dashboard/mentors']);
  }

  goBack(): void {
    this.router.navigate(['/dashboard/mentors']); // O a la página anterior, ej. perfil del mentor
  }

  // Enlaces de texto para las secciones modales
  openContactPage(): void {
    // Implementar navegación a página de contacto
    console.log('Navegar a página de contacto');
  }

  openTermsPage(): void {
    // Implementar navegación a términos y condiciones
    console.log('Navegar a términos y condiciones');
  }

  openNotificationSettings(): void {
    // Implementar navegación a configuración de notificaciones
    console.log('Navegar a configuración de notificaciones');
  }

  // Nuevo método para navegar a días anteriores
  navigateToPreviousDays(): void {
    this.currentDayOffset -= this.diasDeNavegacion;
    // Evitar navegar a fechas anteriores a hoy
    if (this.currentDayOffset < 0) {
      this.currentDayOffset = 0;
      return;
    }
    this.generateDisplayableDates(this.diasDeNavegacion);
    this.selectedDate = null;
    this.selectedTimeSlot = null;
    this.displayableTimeSlots = [];
  }

  // Nuevo método para navegar a días siguientes
  navigateToNextDays(): void {
    this.currentDayOffset += this.diasDeNavegacion;
    this.generateDisplayableDates(this.diasDeNavegacion);
    this.selectedDate = null;
    this.selectedTimeSlot = null;
    this.displayableTimeSlots = [];
  }

  // Nuevo método para obtener el rango de fechas actual
  getCurrentDateRange(): string {
    if (this.displayableDates.length === 0) return '';

    const firstDate = this.displayableDates[0].date;
    const lastDate = this.displayableDates[this.displayableDates.length - 1].date;

    const firstFormatted = formatDate(firstDate, 'dd MMM', 'es-PE');
    const lastFormatted = formatDate(lastDate, 'dd MMM yyyy', 'es-PE');

    return `${firstFormatted} - ${lastFormatted}`;
  }

  getProfilePhotoUrl(): string {
    if (this.mentor?.personalSection?.photoBase64) {
      return this.mentor.personalSection.photoBase64;
    }
    return this.mentor?.photo || "assets/img/user-photo-default.png";
  }

}